// REQ-P1-06b: システム常駐Context向けの契約型付きAPI。

import { api, type paths } from './client';
import type { operations } from './types.gen';
import { ApiStatusError } from './templates';

type MaintenanceResponse = NonNullable<
  paths['/system/maintenance']['get']['responses'][200]['content']
>['application/json'];
type AdminFeatureFlagsQuery = NonNullable<
  paths['/admin/feature-flags']['get']['parameters']['query']
>;
type AdminFeatureFlagsResponse = NonNullable<
  paths['/admin/feature-flags']['get']['responses'][200]['content']
>['application/json'];
type BugReportStatusResponse = NonNullable<
  paths['/bug-reports/status']['get']['responses'][200]['content']
>['application/json'];
type ManualListResponse = NonNullable<
  paths['/admin/manuals']['get']['responses'][200]['content']
>['application/json'];
type ManualDetailResponse = NonNullable<
  paths['/admin/manuals/{slug}']['get']['responses'][200]['content']
>['application/json'];
type BugReportsQuery = NonNullable<
  paths['/bug-reports']['get']['parameters']['query']
>;
type BugReportsResponse = NonNullable<
  paths['/bug-reports']['get']['responses'][200]['content']
>['application/json'];
type CreateBugReportRequest = NonNullable<
  paths['/bug-reports']['post']['requestBody']
>['content']['application/json'];
type CreateBugReportResponse = NonNullable<
  paths['/bug-reports']['post']['responses'][201]['content']
>['application/json'];
type UpdateBugReportResolutionRequest = NonNullable<
  paths['/bug-reports/{bugId}/resolution']['patch']['requestBody']
>['content']['application/json'];
type UpdateBugReportResolutionResponse = NonNullable<
  paths['/bug-reports/{bugId}/resolution']['patch']['responses'][200]['content']
>['application/json'];
type StorageProxyUrlQuery = NonNullable<
  paths['/storage/proxy-url']['get']['parameters']['query']
>;
type StorageProxyUrlResponse = NonNullable<
  paths['/storage/proxy-url']['get']['responses'][200]['content']
>['application/json'];
type StoragePresignGetQuery = operations['createStoragePresignGet']['parameters']['query'];
type StoragePresignGetResponse = NonNullable<
  operations['createStoragePresignGet']['responses'][200]['content']
>['application/json'];
type PublicPolicyKey = NonNullable<
  paths['/policies/{key}']['get']['parameters']['path']
>['key'];
type PublicPolicyResponse = NonNullable<
  paths['/policies/{key}']['get']['responses'][200]['content']
>['application/json'];
type AdminPolicyHistoryQuery = NonNullable<
  paths['/admin/policies/{key}/history']['get']['parameters']['query']
>;
type AdminPolicyHistoryResponse = NonNullable<
  paths['/admin/policies/{key}/history']['get']['responses'][200]['content']
>['application/json'];
type UpdateAdminPolicyRequest = NonNullable<
  paths['/admin/policies/{key}']['put']['requestBody']
>['content']['application/json'];
type UpdateAdminPolicyResponse = NonNullable<
  paths['/admin/policies/{key}']['put']['responses'][200]['content']
>['application/json'];
type AdminMobileReleasePolicyQuery = NonNullable<
  operations['getAdminMobileReleasePolicy']['parameters']['query']
>;
type AdminMobileReleasePolicyResponse = NonNullable<
  operations['getAdminMobileReleasePolicy']['responses'][200]['content']
>['application/json'];
type UpdateAdminMobileReleasePolicyRequest = NonNullable<
  operations['updateAdminMobileReleasePolicy']['requestBody']
>['content']['application/json'];
type UpdateAdminMobileReleasePolicyResponse = NonNullable<
  operations['updateAdminMobileReleasePolicy']['responses'][200]['content']
>['application/json'];
type OperationalEmailPreviewRequest = NonNullable<
  operations['previewOperationalEmailRecipients']['requestBody']
>['content']['application/json'];
type OperationalEmailSearchQuery = NonNullable<
  operations['searchOperationalEmailRecipients']['parameters']['query']
>;
type OperationalEmailCampaignsQuery = NonNullable<
  operations['listOperationalEmailCampaigns']['parameters']['query']
>;
type OperationalEmailCampaignRequest = NonNullable<
  operations['createOperationalEmailCampaign']['requestBody']
>['content']['application/json'];
type ExportAdminActionLogsCsvRequest = NonNullable<
  operations['exportAdminActionLogsCsv']['requestBody']
>['content']['application/json'];
type AdminActionLogsQuery = NonNullable<operations['listAdminActionLogs']['parameters']['query']>;
type AdminActionLogsResponse = NonNullable<
  operations['listAdminActionLogs']['responses'][200]['content']
>['application/json'];
type AdminActionLogAnalyticsQuery = NonNullable<
  operations['getAdminActionLogAnalytics']['parameters']['query']
>;
type AdminActionLogAnalyticsResponse = NonNullable<
  operations['getAdminActionLogAnalytics']['responses'][200]['content']
>['application/json'];
type AdminObservabilityQuery = NonNullable<
  operations['getAdminObservabilitySnapshot']['parameters']['query']
>;
type AdminObservabilityResponse = NonNullable<
  operations['getAdminObservabilitySnapshot']['responses'][200]['content']
>['application/json'];
type CreateEventOptionRequest = NonNullable<
  operations['createEventOption']['requestBody']
>['content']['application/json'];
type DeleteEventOptionPath = operations['deleteEventOption']['parameters']['path'];

type ManualTarget = 'admin' | 'artist';
type ApiTextResult = {
  data?: string | unknown;
  error?: string | unknown;
  response: Response;
};

function ensureData<T>(data: T | undefined, status: number, ok: boolean): T {
  if (!ok || !data) throw new ApiStatusError(status);
  return data;
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

async function throwApiErrorWithPayload(
  response: Response,
  error: unknown,
  fallbackMessage: string
): Promise<never> {
  const payload = await readJsonObject(response);
  const message =
    (typeof payload.message === 'string' && payload.message) ||
    (typeof payload.error === 'string' && payload.error) ||
    getApiErrorMessage(error) ||
    fallbackMessage;
  const statusError = new ApiStatusError(response.status);
  statusError.message = message;
  throw statusError;
}

function parseJsonText(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw !== 'string' || !raw.trim()) return {};
  const parsed = JSON.parse(raw);
  return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
}

async function readJsonObject(response: Response): Promise<Record<string, unknown>> {
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function parseOperationalEmailResult(
  result: ApiTextResult,
  fallbackMessage: string
): Record<string, unknown> {
  let payload: Record<string, unknown> = {};
  try {
    payload = parseJsonText(result.data);
  } catch {
    payload = {};
  }
  if (!result.response.ok) {
    throwApiError(result.response.status, result.error || payload, fallbackMessage);
  }
  return payload;
}

export async function getMaintenanceState(): Promise<{
  ok: boolean;
  status: number;
  data: MaintenanceResponse | undefined;
}> {
  const { data, response } = await api.GET('/system/maintenance', {
    credentials: 'omit',
    cache: 'no-store',
  });
  return {
    ok: response.ok,
    status: response.status,
    data: data as MaintenanceResponse | undefined,
  };
}

export async function getBugReportStatus(options: { signal?: AbortSignal } = {}) {
  const { data, response } = await api.GET('/bug-reports/status', {
    signal: options.signal,
  });
  return ensureData(data as BugReportStatusResponse | undefined, response.status, response.ok);
}

export async function listManuals(target: ManualTarget): Promise<ManualListResponse> {
  const result =
    target === 'artist'
      ? await api.GET('/artist/manuals')
      : await api.GET('/admin/manuals');
  if (!result.response.ok || !result.data) {
    throwApiError(result.response.status, result.error, 'マニュアル一覧の取得に失敗しました');
  }
  return result.data as ManualListResponse;
}

export async function getManual(
  target: ManualTarget,
  slug: string
): Promise<ManualDetailResponse> {
  const result =
    target === 'artist'
      ? await api.GET('/artist/manuals/{slug}', { params: { path: { slug } } })
      : await api.GET('/admin/manuals/{slug}', { params: { path: { slug } } });
  if (!result.response.ok || !result.data) {
    throwApiError(result.response.status, result.error, 'マニュアルの取得に失敗しました');
  }
  return result.data as ManualDetailResponse;
}

export async function createStorageProxyUrl(
  query: StorageProxyUrlQuery
): Promise<StorageProxyUrlResponse> {
  const { data, error, response } = await api.GET('/storage/proxy-url', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '画像URLの取得に失敗しました');
  }
  return data as StorageProxyUrlResponse;
}

export async function createStoragePresignGet(
  query: StoragePresignGetQuery
): Promise<StoragePresignGetResponse> {
  const { data, error, response } = await api.GET('/storage/presign-get', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'ダウンロードURLの取得に失敗しました');
  }
  return data as StoragePresignGetResponse;
}

export async function getPublicPolicy(key: PublicPolicyKey): Promise<PublicPolicyResponse> {
  const { data, error, response } = await api.GET('/policies/{key}', {
    params: { path: { key } },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '取得に失敗しました');
  }
  return data as PublicPolicyResponse;
}

export async function getAdminPolicyHistory(
  key: PublicPolicyKey,
  query: AdminPolicyHistoryQuery = {}
): Promise<AdminPolicyHistoryResponse> {
  const { data, error, response } = await api.GET('/admin/policies/{key}/history', {
    params: { path: { key }, query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '履歴の取得に失敗しました');
  }
  return data as AdminPolicyHistoryResponse;
}

export async function updateAdminPolicy(
  key: PublicPolicyKey,
  body: UpdateAdminPolicyRequest
): Promise<UpdateAdminPolicyResponse> {
  const { data, error, response } = await api.PUT('/admin/policies/{key}', {
    params: { path: { key } },
    body,
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '保存に失敗しました');
  }
  return data as UpdateAdminPolicyResponse;
}

export async function getAdminMobileReleasePolicy(
  query: AdminMobileReleasePolicyQuery = {}
): Promise<AdminMobileReleasePolicyResponse> {
  const { data, error, response } = await api.GET('/admin/mobile-release', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'モバイル更新設定を取得できません。');
  }
  return data as AdminMobileReleasePolicyResponse;
}

export async function updateAdminMobileReleasePolicy(
  body: UpdateAdminMobileReleasePolicyRequest
): Promise<UpdateAdminMobileReleasePolicyResponse> {
  const { data, error, response } = await api.PUT('/admin/mobile-release', { body });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'モバイル更新設定を保存できません。');
  }
  return data as UpdateAdminMobileReleasePolicyResponse;
}

export type AdminMacRelease = {
  id: string;
  version: string;
  buildNumber: string;
  fileName: string;
  fileSizeBytes: number;
  sparkleEdSignature: string;
  releaseNotes?: string;
  minimumSystemVersion?: string;
  minimumSupportedVersion?: string;
  required?: boolean;
  isLatest?: boolean;
  status?: string;
  uploadedByName?: string;
  publishedAt?: string;
  deletedAt?: string | null;
};

export type AdminMacReleaseResponse = {
  success: boolean;
  releases?: AdminMacRelease[];
  release?: AdminMacRelease;
  message?: string;
};

export type AdminMacReleaseUploadProgress = {
  loaded: number;
  total: number;
  percent: number | null;
  lengthComputable: boolean;
};

export type AdminMacReleaseUploadProgressHandler = (progress: AdminMacReleaseUploadProgress) => void;

function resolveAdminRawApiUrl(path: string): string {
  const origin = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${origin}/api${normalizedPath}`;
}

function readCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function getNativeAuthBridgeHeaders(): Array<[string, string]> {
  if (typeof window === 'undefined') return [];
  const bridge = (window as any).__CASTICART_NATIVE_AUTH__;
  if (!bridge || typeof bridge !== 'object') return [];

  const headers: Array<[string, string]> = [];
  const authorization = String(
    bridge.authorizationHeader || bridge.authorization || bridge.Authorization || ''
  ).trim();
  if (/^Bearer\s+\S+/i.test(authorization)) {
    headers.push(['Authorization', authorization]);
  }

  const client = String(bridge.client || 'native').trim();
  const app = String(bridge.app || 'casticart_admin_mac').trim();
  const platform = String(bridge.platform || 'macos').trim();
  const deviceId = String(bridge.deviceId || bridge.deviceID || '').trim();
  if (client) headers.push(['x-casticart-client', client]);
  if (app) headers.push(['x-casticart-app', app]);
  if (platform) headers.push(['x-casticart-platform', platform]);
  if (deviceId) headers.push(['x-casticart-device-id', deviceId]);
  return headers;
}

async function ensureCsrfCookie(_targetUrl: string, { forceRefresh = false } = {}): Promise<string> {
  const cookieName = process.env.REACT_APP_CSRF_COOKIE || 'casticart-csrf';
  const existing = readCookie(cookieName);
  if (existing && !forceRefresh) return existing;
  try {
    const apiClient = api as any;
    const { data } = await apiClient.GET('/csrf-token', { cache: 'no-store' });
    return String(data?.token || readCookie(cookieName) || '').trim();
  } catch (_error) {
    return readCookie(cookieName);
  }
}

function createRequestId(): string {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function parseXhrJson(xhr: XMLHttpRequest): Record<string, unknown> | null {
  if (!xhr.responseText) return null;
  try {
    return JSON.parse(xhr.responseText) as Record<string, unknown>;
  } catch (_error) {
    return null;
  }
}

async function uploadAdminMacReleaseViaXhr(
  formData: FormData,
  onProgress?: AdminMacReleaseUploadProgressHandler,
  csrfTokenOverride?: string
): Promise<AdminMacReleaseResponse> {
  const url = resolveAdminRawApiUrl('/admin/mac-release');
  const csrfHeader = process.env.REACT_APP_CSRF_HEADER || 'x-casticart-csrf';
  const csrfToken = csrfTokenOverride || await ensureCsrfCookie(url);
  const idempotencyHeader = process.env.REACT_APP_IDEMPOTENCY_HEADER || 'Idempotency-Key';

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    if (csrfToken) xhr.setRequestHeader(csrfHeader, csrfToken);
    xhr.setRequestHeader(idempotencyHeader, `mac-release-upload:${createRequestId()}`);
    getNativeAuthBridgeHeaders().forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    xhr.upload.onprogress = (event) => {
      const loaded = Number(event.loaded || 0);
      const total = Number(event.total || 0);
      const lengthComputable = Boolean(event.lengthComputable && total > 0);
      onProgress?.({
        loaded,
        total: lengthComputable ? total : 0,
        percent: lengthComputable ? Math.min(100, Math.max(0, Math.round((loaded / total) * 100))) : null,
        lengthComputable,
      });
    };

    xhr.onload = () => {
      const data = parseXhrJson(xhr);
      if (xhr.status >= 200 && xhr.status < 300 && data) {
        resolve(data as AdminMacReleaseResponse);
        return;
      }
      const message =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        'MacアプリDMGを登録できません。';
      const statusError = new ApiStatusError(xhr.status);
      statusError.message = message;
      reject(statusError);
    };
    xhr.onerror = () => {
      const statusError = new ApiStatusError(xhr.status || 0);
      statusError.message = '通信エラーによりMacアプリDMGを登録できません。';
      reject(statusError);
    };
    xhr.onabort = () => {
      const statusError = new ApiStatusError(xhr.status || 0);
      statusError.message = 'MacアプリDMGのアップロードを中断しました。';
      reject(statusError);
    };
    xhr.send(formData);
  });
}

export async function getAdminMacReleases(): Promise<AdminMacReleaseResponse> {
  const apiClient = api as any;
  const { data, error, response } = await apiClient.GET('/admin/mac-release');
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'Macアプリ更新一覧を取得できません。');
  }
  return data as AdminMacReleaseResponse;
}

export async function uploadAdminMacRelease(formData: FormData): Promise<AdminMacReleaseResponse> {
  const apiClient = api as any;
  const { data, error, response } = await apiClient.POST('/admin/mac-release', {
    body: formData,
    bodySerializer: (body: FormData) => body,
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'MacアプリDMGを登録できません。');
  }
  return data as AdminMacReleaseResponse;
}

export async function uploadAdminMacReleaseWithProgress(
  formData: FormData,
  onProgress?: AdminMacReleaseUploadProgressHandler
): Promise<AdminMacReleaseResponse> {
  try {
    return await uploadAdminMacReleaseViaXhr(formData, onProgress);
  } catch (error) {
    if (error instanceof ApiStatusError && error.status === 403 && /csrf/i.test(error.message || '')) {
      const csrfToken = await ensureCsrfCookie(resolveAdminRawApiUrl('/admin/mac-release'), { forceRefresh: true });
      return uploadAdminMacReleaseViaXhr(formData, onProgress, csrfToken);
    }
    throw error;
  }
}

export async function deleteAdminMacRelease(id: string): Promise<AdminMacReleaseResponse> {
  const apiClient = api as any;
  const { data, error, response } = await apiClient.DELETE('/admin/mac-release/{id}', {
    params: { path: { id } },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'Macアプリリリースを削除できません。');
  }
  return data as AdminMacReleaseResponse;
}

export async function listEventOptions(): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/event-options', { parseAs: 'text' });
  if (!response.ok) throw new ApiStatusError(response.status);
  try {
    const rows = typeof data === 'string' && data ? JSON.parse(data) : [];
    return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
  } catch (_error) {
    return [];
  }
}

export async function createEventOption(
  body: CreateEventOptionRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/event-options', {
    body,
    parseAs: 'text',
  });
  return parseOperationalEmailResult(result, 'オプションの追加に失敗しました。');
}

export async function deleteEventOption(id: DeleteEventOptionPath['id']): Promise<Record<string, unknown>> {
  const result = await api.DELETE('/event-options/{id}', {
    params: { path: { id } },
    parseAs: 'text',
  });
  return parseOperationalEmailResult(result, 'オプションの削除に失敗しました。');
}

export async function listBugReports(
  query: BugReportsQuery = {}
): Promise<BugReportsResponse> {
  const { data, error, response } = await api.GET('/bug-reports', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'バグ報告の取得に失敗しました');
  }
  return data as BugReportsResponse;
}

export async function createBugReport(
  body: CreateBugReportRequest
): Promise<CreateBugReportResponse> {
  const { data, error, response } = await api.POST('/bug-reports', { body });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '送信に失敗しました');
  }
  return data as CreateBugReportResponse;
}

export async function updateBugReportResolution(
  bugId: string,
  body: UpdateBugReportResolutionRequest
): Promise<UpdateBugReportResolutionResponse> {
  const { data, error, response } = await api.PATCH('/bug-reports/{bugId}/resolution', {
    params: { path: { bugId } },
    body,
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '解決状態の更新に失敗しました');
  }
  return data as UpdateBugReportResolutionResponse;
}

export async function listAdminFeatureFlags(
  query: AdminFeatureFlagsQuery = {},
  options: { signal?: AbortSignal } = {}
) {
  const { data, response } = await api.GET('/admin/feature-flags', {
    params: { query },
    signal: options.signal,
  });
  return ensureData(data as AdminFeatureFlagsResponse | undefined, response.status, response.ok);
}

export async function listOperationalEmailCampaigns(
  query: OperationalEmailCampaignsQuery = {}
): Promise<Record<string, unknown>> {
  const result = await api.GET('/admin/operational-emails', {
    params: { query },
    parseAs: 'text',
  });
  return parseOperationalEmailResult(result, '履歴の取得に失敗しました');
}

export async function searchOperationalEmailRecipients(
  query: OperationalEmailSearchQuery,
  options: { signal?: AbortSignal } = {}
): Promise<Record<string, unknown>[]> {
  const result = await api.GET('/admin/operational-emails/recipients/search', {
    params: { query },
    signal: options.signal,
    parseAs: 'text',
  });
  const payload = parseOperationalEmailResult(result, '検索に失敗しました');
  return Array.isArray(payload.recipients) ? (payload.recipients as Record<string, unknown>[]) : [];
}

export async function previewOperationalEmailRecipients(
  body: OperationalEmailPreviewRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/operational-emails/preview', {
    body,
    parseAs: 'text',
  });
  return parseOperationalEmailResult(result, '対象確認に失敗しました');
}

export async function createOperationalEmailCampaign(
  body: OperationalEmailCampaignRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/operational-emails', {
    body,
    parseAs: 'text',
  });
  return parseOperationalEmailResult(result, '送信に失敗しました');
}

export async function getOperationalEmailCampaign(
  campaignId: string
): Promise<Record<string, unknown>> {
  const result = await api.GET('/admin/operational-emails/{campaignId}', {
    params: { path: { campaignId } },
    parseAs: 'text',
  });
  return parseOperationalEmailResult(result, '送信詳細の取得に失敗しました');
}

export async function exportAdminActionLogsCsv(
  body: ExportAdminActionLogsCsvRequest
): Promise<void> {
  const { error, response } = await api.POST('/admin/action-logs/csv-export', { body });
  if (!response.ok) {
    throwApiError(response.status, error, 'CSV出力ログの送信に失敗しました');
  }
}

export async function listAdminActionLogs(
  query: AdminActionLogsQuery
): Promise<AdminActionLogsResponse> {
  const { data, error, response } = await api.GET('/admin/action-logs', {
    params: { query },
  });
  if (!response.ok || !data) {
    await throwApiErrorWithPayload(response, error, 'ログの取得に失敗しました');
  }
  return data as AdminActionLogsResponse;
}

export async function getAdminActionLogAnalytics(
  query: AdminActionLogAnalyticsQuery
): Promise<AdminActionLogAnalyticsResponse> {
  const { data, error, response } = await api.GET('/admin/action-logs/analytics', {
    params: { query },
  });
  if (!response.ok || !data) {
    await throwApiErrorWithPayload(response, error, '分析データの取得に失敗しました');
  }
  return data as AdminActionLogAnalyticsResponse;
}

export async function getAdminObservabilitySnapshot(
  query: AdminObservabilityQuery
): Promise<AdminObservabilityResponse> {
  const { data, error, response } = await api.GET('/admin/action-logs/observability', {
    params: { query },
  });
  if (!response.ok || !data) {
    await throwApiErrorWithPayload(response, error, '運用メトリクスの取得に失敗しました');
  }
  return data as AdminObservabilityResponse;
}
